// Settings → Site settings → Site: the picked site's name, domains, timezone and currency (owners
// and admins), and its Danger zone (admins: delete, a soft delete an admin can undo). Also the
// admins' "Deleted sites" list with Restore. The API is projects.ts (PATCH/DELETE /api/projects/<id>).

export interface SiteView {
  id: string;
  name: string;
  kind: "site" | "app";
  org_id: string;
  org: string;
  domains: string[];
  sandbox: boolean;
  timezone: string;
  currency: string;
  has_revenue: boolean;
}

interface DeletedSite {
  id: string;
  name: string;
  kind: "site" | "app";
  org: string;
  domains: string[];
  deleted_at: number | null;
  deleted_by: string | null;
}

class ApiError extends Error {
  constructor(message: string, public detail: Record<string, unknown>) {
    super(message);
  }
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = (await res.json().catch(() => ({ error: `HTTP ${res.status}` }))) as Record<string, unknown>;
  if (!res.ok) throw new ApiError(typeof out.error === "string" ? out.error : `HTTP ${res.status}`, out);
  return out as T;
}

const CURRENCIES = ["AED", "SAR", "QAR", "KWD", "BHD", "OMR", "USD", "EUR", "GBP", "INR", "PKR", "EGP"];

function zones(current: string): string[] {
  const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  return all.includes(current) ? all : [current, ...all];
}

function problem(e: unknown): string {
  const err = e as ApiError;
  const d = err.detail ?? {};
  switch (err.message) {
    case "bad_name":
      return "Give it a name (up to 80 characters).";
    case "bad_domain":
      return `“${String(d.domain ?? "")}” isn't a domain. Use a hostname like example.com.`;
    case "no_domains":
      return "A site needs at least one domain.";
    case "too_many_domains":
      return "That's too many domains for one site.";
    case "domain_taken":
      return d.project_name
        ? `${String(d.domain)} is already used by ${String(d.project_name)}. Remove it there first (or delete that site).`
        : `${String(d.domain)} is already used by another site.`;
    case "bad_timezone":
      return "Pick a timezone from the list.";
    case "bad_currency":
      return "Pick a three-letter currency code, like AED or USD.";
    case "read_only":
    case "not_allowed":
      return "You can't change this site.";
    case "unknown_project":
      return "This site no longer exists. Reload the page.";
    default:
      return `Something went wrong (${err.message}).`;
  }
}

/**
 * allowed_domains as the person thinks of them: "example.com" stands for itself and its www.
 * variant, so "www.example.com" is hidden when "example.com" is listed too.
 */
export function displayDomains(stored: string[]): string[] {
  const set = new Set(stored.map((d) => d.toLowerCase()));
  return stored.filter((d) => !(d.toLowerCase().startsWith("www.") && set.has(d.toLowerCase().slice(4))));
}

const DOMAIN_RE = /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** What the server stores for a typed domain (projects.ts normDomain), or null when it isn't one. */
export function typedDomain(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  const wild = t.startsWith("*.");
  const d = (wild ? t.slice(2) : t)
    .replace(/^[a-z]+:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.$/, "");
  if (wild) return DOMAIN_RE.test(d) && !d.startsWith("www.") ? `*.${d}` : null;
  const bare = d.replace(/^www\./, "");
  return DOMAIN_RE.test(bare) ? bare : null;
}

export interface SiteCardOpts {
  el: HTMLElement;
  site: { id: string; name: string };
  admin: boolean;
  current: () => boolean;
  /** After a save: the new name, so the toolbar, the site picker and Home show it. */
  onSaved: (v: SiteView) => void;
  /** After a delete. */
  onDeleted: (id: string) => void;
}

export async function renderSiteCard(o: SiteCardOpts): Promise<void> {
  o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Site</h2></div><div class="acct-msg">Loading…</div></div>`;
  let v: SiteView;
  try {
    v = await call<SiteView>("GET", `projects/${encodeURIComponent(o.site.id)}`);
  } catch (e) {
    if (!o.current()) return;
    o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Site</h2></div><div class="acct-msg error">Couldn't load this site (${esc((e as Error).message)}).</div></div>`;
    return;
  }
  if (o.current()) drawSite(o, v);
}

function drawSite(o: SiteCardOpts, v: SiteView): void {
  const saved = displayDomains(v.domains);
  const domains = [...saved];
  const word = v.kind === "site" ? "site" : "app";
  const chips = () =>
    domains
      .map(
        (d, i) => `<li class="site-dom">
          <span class="site-dom-name mono">${esc(d)}</span>${d.startsWith("*.") ? "" : `<span class="muted site-dom-www">+ www</span>`}
          <span class="site-dom-act">${i === 0 ? `<span class="pill good">Primary</span>` : `<button class="link" type="button" data-primary="${i}">Make primary</button>`}
          <button class="link danger site-dom-x" type="button" data-remove="${i}" aria-label="Remove ${esc(d)}"${domains.length === 1 ? " disabled" : ""}>Remove</button></span>
        </li>`,
      )
      .join("");
  o.el.innerHTML = `<div class="card site-card">
  <div class="card-head"><h2>Site</h2><span class="muted site-org">${esc(v.org)} · <span class="mono">${esc(v.id)}</span></span></div>
  <form class="add-form site-form" novalidate>
    <label class="fld"><span>Name</span><input class="in" name="name" maxlength="80" required value="${esc(v.name)}" autocomplete="off"></label>
    ${
      v.kind === "site"
        ? `<div class="fld"><span>Domains</span>
      <ul class="site-doms" aria-label="Domains"></ul>
      <div class="site-dom-add"><input class="in" name="new-domain" placeholder="Add a domain, e.g. shop.example.com" inputmode="url" autocapitalize="off" spellcheck="false" autocomplete="off" aria-label="Add a domain"><button class="btn" type="button" data-add-domain>Add</button></div>
      <span class="fld-help">Events are accepted from each domain and its www. version (use *.example.com for every subdomain). The primary domain is the one uptime checks fetch.${v.sandbox ? " Shopify's sandboxed checkout pages stay allowed too." : ""}</span>
      <div class="err-note site-primary-note" hidden></div>
    </div>`
        : `<div class="fld"><span>Domains</span><div class="site-app">App — no website domain. Apps send events from the MonoLog SDK, not from a web page.</div></div>`
    }
    <label class="fld"><span>Timezone</span><select class="in" name="timezone">${zones(v.timezone)
      .map((z) => `<option${z === v.timezone ? " selected" : ""}>${esc(z)}</option>`)
      .join("")}</select><span class="fld-help">Where a day starts in this ${word}'s reports. Days already summarised keep the old timezone.</span></label>
    ${
      v.has_revenue
        ? `<label class="fld"><span>Currency</span><select class="in" name="currency">${[...new Set([v.currency, ...CURRENCIES])]
            .map((c) => `<option${c === v.currency ? " selected" : ""}>${esc(c)}</option>`)
            .join("")}</select><span class="fld-help">Revenue amounts are shown in this currency, as the ${word} sends them. They are never converted.</span></label>`
        : ""
    }
    <div class="form-err" hidden></div>
    <div class="btns site-btns"><button class="btn primary" type="submit">Save</button><button class="btn" type="button" data-reset>Reset</button><span class="acct-msg" role="status"></span></div>
  </form>
  ${
    o.admin
      ? `<section class="site-danger" aria-labelledby="site-danger-h">
    <h3 id="site-danger-h">Danger zone</h3>
    <div class="site-danger-row"><p>Delete this ${word}: it stops collecting and disappears from Home, the project list and alerts. Its data is kept, and an admin can restore it.</p>
    <button class="btn site-del-open" type="button" data-delete>Delete ${word}…</button></div>
  </section>`
      : ""
  }
</div>`;
  const form = o.el.querySelector<HTMLFormElement>(".site-form")!;
  const list = form.querySelector<HTMLUListElement>(".site-doms");
  const note = form.querySelector<HTMLElement>(".site-primary-note");
  const err = form.querySelector<HTMLElement>(".form-err")!;
  const status = form.querySelector<HTMLElement>("[role=status]")!;
  const showErr = (m: string | null) => {
    err.textContent = m ?? "";
    err.hidden = !m;
  };
  const drawDomains = () => {
    if (!list) return;
    list.innerHTML = chips();
    if (note) {
      const moved = domains[0] !== saved[0];
      note.hidden = !moved;
      note.textContent = moved
        ? `New primary domain: ${domains[0]}. The tracking snippet keeps working (same key), but the site must send from ${domains[0]} for its events to count. Uptime checks move to ${domains[0]}.`
        : "";
    }
    for (const b of list.querySelectorAll<HTMLButtonElement>("[data-remove]"))
      b.addEventListener("click", () => {
        domains.splice(Number(b.dataset.remove), 1);
        drawDomains();
      });
    for (const b of list.querySelectorAll<HTMLButtonElement>("[data-primary]"))
      b.addEventListener("click", () => {
        const [d] = domains.splice(Number(b.dataset.primary), 1);
        domains.unshift(d!);
        drawDomains();
      });
  };
  drawDomains();
  const input = form.querySelector<HTMLInputElement>("input[name=new-domain]");
  const add = () => {
    if (!input || !input.value.trim()) return;
    const d = typedDomain(input.value);
    if (!d) return showErr(`“${input.value.trim()}” isn't a domain. Use a hostname like example.com.`);
    if (domains.includes(d)) return showErr(`${d} is already in the list.`);
    showErr(null);
    domains.push(d);
    input.value = "";
    drawDomains();
  };
  form.querySelector("[data-add-domain]")?.addEventListener("click", add);
  input?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add();
    }
  });
  form.querySelector("[data-reset]")!.addEventListener("click", () => drawSite(o, v));
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    showErr(null);
    if (input?.value.trim()) add(); // a typed but un-added domain counts
    if (!err.hidden) return;
    const f = new FormData(form);
    const body: Record<string, unknown> = {};
    const name = String(f.get("name") ?? "").trim();
    if (name !== v.name) body.name = name;
    const tz = String(f.get("timezone") ?? "");
    if (tz && tz !== v.timezone) body.timezone = tz;
    const cur = f.get("currency");
    if (cur !== null && String(cur) !== v.currency) body.currency = String(cur);
    if (v.kind === "site" && domains.join("\n") !== saved.join("\n")) body.domains = domains;
    if (!Object.keys(body).length) {
      status.textContent = "Nothing to save.";
      return;
    }
    const btn = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    btn.disabled = true;
    try {
      const next = await call<SiteView>("PATCH", `projects/${encodeURIComponent(v.id)}`, body);
      if (!o.current()) return;
      drawSite(o, next);
      const s = o.el.querySelector<HTMLElement>(".site-form [role=status]");
      if (s) s.textContent = "Saved.";
      o.onSaved(next);
    } catch (ex) {
      btn.disabled = false;
      showErr(problem(ex));
    }
  });
  o.el.querySelector("[data-delete]")?.addEventListener("click", (e) => openDelete(v, e.currentTarget as HTMLElement, o.onDeleted));
}

/** The delete confirmation: type the name to enable Delete. */
function openDelete(v: SiteView, returnTo: HTMLElement, onDeleted: (id: string) => void): void {
  const word = v.kind === "site" ? "site" : "app";
  const back = document.createElement("div");
  back.className = "share-back";
  back.innerHTML = `<section class="card share-card site-del" role="dialog" aria-modal="true" aria-labelledby="site-del-h">
    <div class="card-head"><h2 id="site-del-h">Delete ${esc(v.name)}?</h2></div>
    <p class="site-del-lead">Stops collecting immediately; existing data is kept and can be restored by an admin.</p>
    <ul class="site-del-list">
      <li>Events sent with its key are refused from now on (collectors may take up to a minute to notice).</li>
      <li>Its server and proxy keys are revoked; a restore brings them back.</li>
      <li>Uptime checks and alerts stop, and it's hidden from Home, the project list and alerts.</li>
    </ul>
    <label class="fld"><span>Type <b>${esc(v.name)}</b> to confirm</span><input class="in" name="confirm" autocomplete="off" spellcheck="false"></label>
    <div class="form-err" hidden></div>
    <div class="btns add-btns"><button class="btn" type="button" data-cancel>Cancel</button><button class="btn site-del-confirm" type="button" data-confirm disabled>Delete ${word}</button></div>
  </section>`;
  document.body.append(back);
  const input = back.querySelector<HTMLInputElement>("input[name=confirm]")!;
  const ok = back.querySelector<HTMLButtonElement>("[data-confirm]")!;
  const err = back.querySelector<HTMLElement>(".form-err")!;
  const close = () => {
    back.remove();
    document.removeEventListener("keydown", onKey);
    returnTo.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  back.addEventListener("click", (e) => {
    if (e.target === back) close();
  });
  back.querySelector("[data-cancel]")!.addEventListener("click", close);
  input.addEventListener("input", () => (ok.disabled = input.value.trim() !== v.name));
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !ok.disabled) ok.click();
  });
  ok.addEventListener("click", async () => {
    ok.disabled = true;
    try {
      await call("DELETE", `projects/${encodeURIComponent(v.id)}`);
      back.remove();
      document.removeEventListener("keydown", onKey);
      onDeleted(v.id);
    } catch (e) {
      ok.disabled = false;
      err.textContent = problem(e);
      err.hidden = false;
    }
  });
  input.focus();
}

const when = (t: number | null) => (t ? new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "Unknown date");

/** Admins: deleted sites and apps, each with Restore. */
export async function renderDeletedSites(o: { el: HTMLElement; current: () => boolean; onRestored: (id: string) => void }): Promise<void> {
  let list: DeletedSite[];
  try {
    list = await call<DeletedSite[]>("GET", "projects/deleted");
  } catch (e) {
    if (!o.current()) return;
    o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Deleted sites</h2></div><div class="acct-msg error">Couldn't load deleted sites (${esc((e as Error).message)}).</div></div>`;
    return;
  }
  if (!o.current()) return;
  o.el.innerHTML = `<div class="card site-deleted">
  <div class="card-head"><h2>Deleted sites</h2></div>
  ${
    list.length
      ? `<p class="muted">Restoring brings a site back with its data, settings and keys, and it starts collecting again.</p>
    <ul class="site-deleted-list">${list
      .map(
        (d) => `<li><div class="site-deleted-who"><b>${esc(d.name)}</b> <span class="muted">${esc(d.org)} · ${d.kind === "app" ? "app" : esc(displayDomains(d.domains)[0] ?? "no domain")}</span>
        <span class="muted site-deleted-when">Deleted ${esc(when(d.deleted_at))}${d.deleted_by ? ` by ${esc(d.deleted_by)}` : ""}</span></div>
        <button class="btn" type="button" data-restore="${esc(d.id)}">Restore</button><span class="acct-msg error" role="status"></span></li>`,
      )
      .join("")}</ul>`
      : `<div class="acct-msg">No deleted sites.</div>`
  }
</div>`;
  for (const b of o.el.querySelectorAll<HTMLButtonElement>("[data-restore]"))
    b.addEventListener("click", async () => {
      b.disabled = true;
      try {
        await call("POST", `projects/${encodeURIComponent(b.dataset.restore!)}/restore`, {});
        o.onRestored(b.dataset.restore!);
      } catch (e) {
        b.disabled = false;
        const s = b.parentElement?.querySelector<HTMLElement>("[role=status]");
        if (s) s.textContent = problem(e);
      }
    });
}
